import { ClassInfo, Studio, StudioClient, User } from '../types';

const WL_GELIOS = '65d0c78f332c5598ddc2bd28';

export default class UserHelper {
  static STUDENT = 1;
  static STUDIO_OWNER = 0;

  static isConfirmed(user: User, classInfo: ClassInfo): boolean {
    const billingTag = classInfo.billingTag;
    const billingTagDefaultFields = user.billingTagDefaultFields || {};
    const defaults = billingTagDefaultFields[billingTag] || {};
    if (classInfo.confirmed === false) {
      return false;
    }
    if (defaults.confirmPresence) {
      return !!classInfo.confirmed;
    }
    return true;
  }

  static hasRole(user: User, roles: string[], excludeRoot?: boolean): boolean {
    if (!user) {
      return false;
    }
    if (!user.employee) {
      return !excludeRoot;
    }
    for (let i = 0; i < roles.length; i++) {
      if (user.employee.roles.includes(roles[i])) {
        return true;
      }
    }
    return false;
  }

  static getDefaultFunnelId(user: User): string | undefined {
    if (localStorage.getItem('funnelFilter')) {
      const filter = JSON.parse(localStorage.getItem('funnelFilter') || '{}');
      if (filter.funnelId) {
        return filter.funnelId;
      }
    }
    return user.defaultFunnelId;
  }

  static isOwner(user: User): boolean {
    return this.hasRole(user, ['OWNER']);
  }

  static isAdmin(user: User): boolean {
    return this.hasRole(user, ['ADMIN']);
  }

  static isTeacher(user: User): boolean {
    return this.hasRole(user, ['TEACHER'], true);
  }

  static canManageRatePlan(user: User): boolean {
    if (this.isOwnerOrAdmin(user)) {
      return true;
    }
    return this.isPayableTeacher(user);
  }

  static isPayableTeacher(user: User): boolean {
    if (this.hasRole(user, ['TEACHER'], true)) {
      return !!(user.employee?.paymentSources && user.employee.paymentSources.length > 0);
    } else {
      return false;
    }
  }

  static isPayableUser(user: User): boolean {
    if (this.hasRole(user, ['TEACHER'], true)) {
      return !!(
        user.employee?.paymentSources &&
        user.employee.paymentSources.length > 0 &&
        user.employee.canEditPaymentDate
      );
    } else {
      return true;
    }
  }

  static isOwnerOrAdmin(user: User): boolean {
    return this.hasRole(user, ['OWNER', 'ADMIN']);
  }

  // Extensible: add other roles here as deletion access grows
  static canDeleteLesson(user: User): boolean {
    return this.hasRole(user, ['OWNER', 'ADMIN']);
  }

  static canSeeContacts(user: User): boolean {
    if (this.hasRole(user, ['OWNER', 'ADMIN'])) {
      return true;
    }
    const employee = user.employee;
    return !!employee?.canSeeContacts;
  }

  static canMarkAttendance(user: User): boolean {
    if (this.hasRole(user, ['OWNER', 'ADMIN'])) {
      return true;
    }
    const employee = user.employee;
    return !!employee?.canMarkAttendance;
  }

  static canSeeOtherClasses(user: User): boolean {
    if (this.hasRole(user, ['OWNER', 'ADMIN'])) {
      return true;
    }
    const employee = user.employee;
    return !!employee?.canSeeOtherClasses;
  }

  static canSeeRatePlans(user: User): boolean {
    if (this.hasRole(user, ['OWNER', 'ADMIN'])) {
      return true;
    }
    const employee = user.employee;
    return !!employee?.canSeeRatePlans;
  }

  static canEditClientsByTeachers(user: User): boolean {
    if (this.hasRole(user, ['OWNER', 'ADMIN'])) {
      return true;
    }
    const employee = user.employee;
    return !!employee?.canEditClients;
  }

  static canSeeContactInfoTeachers(user: User): boolean {
    if (this.hasRole(user, ['OWNER', 'ADMIN'])) {
      return true;
    }
    const employee = user.employee;
    return !!employee?.canSeeContactInfoTeachers;
  }

  static canSeeClientBalance(user: User): boolean {
    if (this.hasRole(user, ['OWNER', 'ADMIN'])) {
      return true;
    }
    const employee = user.employee;
    return !!employee?.canSeeClientBalance;
  }

  static canRescheduleClasses(user: User): boolean {
    if (this.hasRole(user, ['OWNER', 'ADMIN'])) {
      return true;
    }
    const employee = user.employee;
    return !!employee?.canRescheduleClasses;
  }

  static canCancelClasses(user: User): boolean {
    if (this.hasRole(user, ['OWNER', 'ADMIN'])) {
      return true;
    }
    const employee = user.employee;
    return !!employee?.canCancelClasses;
  }

  static canCreateClient(user: User): boolean {
    if (this.hasRole(user, ['OWNER', 'ADMIN'])) {
      return true;
    }
    const employee = user.employee;
    return !!employee?.canCreateClient;
  }

  static canAddEvaluations(user: User): boolean {
    if (this.hasRole(user, ['OWNER', 'ADMIN'])) {
      return true;
    }
    const employee = user.employee;
    return !!employee?.canAddEvaluations;
  }

  // Mirrors the backend rule (grades.service.js#authorize): an employee - whatever the role - only
  // works with grades of lessons held in a studio (branch) they are assigned to. The owner's own
  // account has no `employee` and is not limited.
  static canAddEvaluationsInStudio(user: User, studioId?: string): boolean {
    if (!this.canAddEvaluations(user)) {
      return false;
    }
    const employee = user.employee;
    if (!employee) {
      return true;
    }
    return !!studioId && (employee.studioIds || []).includes(studioId);
  }

  static canSeeAttendanceInfo(user: User): boolean {
    if (this.hasRole(user, ['OWNER', 'ADMIN'])) {
      return true;
    }
    const employee = user.employee;
    return !!employee?.canSeeAttendanceInfo;
  }

  static canSeeOtherEmployeesSalary(user: User, pageUser: User = {}): boolean {
    if (this.hasRole(user, ['OWNER'])) return true;
    if (this.hasRole(user, ['ADMIN'])) {
      return !!(user.employee?.canSeeOtherEmployeesSalary || user.employee?._id === pageUser._id);
    }
    if (this.hasRole(user, ['TEACHER'])) {
      return user.employee?._id === pageUser._id;
    }
    return false;
  }

  static canEditYourself(user: User): boolean {
    if (this.hasRole(user, ['OWNER', 'ADMIN'])) {
      return true;
    }
    const employee = user.employee;
    return !!employee?.canEditYourself;
  }

  static canEditEmployeePermissions(user: User, pageUser?: User): boolean {
    if (this.hasRole(user, ['OWNER'])) return true;
    if (this.hasRole(user, ['ADMIN'])) {
      return !pageUser || user.employee?._id !== pageUser.employee?._id;
    }
    return false;
  }

  static getMyPaymentSources(user: User): string[] {
    const employee = user.employee;
    if (!employee) {
      return user.paymentSources || [];
    }
    if (employee.paymentSources) {
      return employee.paymentSources;
    }
    return [];
  }

  static getAttenancePaymentPurpose(owner: User, billingTag: string): string {
    const defaultPurpose = 'ATTENDANCE';
    if (owner.billingTagDefaultFields) {
      if (owner.billingTagDefaultFields[billingTag]) {
        return owner.billingTagDefaultFields[billingTag].purpose || defaultPurpose;
      } else {
        return defaultPurpose;
      }
    } else {
      return defaultPurpose;
    }
  }

  static canEditPaymentDate(user: User, pageUser: User | null = null): boolean {
    const employee = user.employee;
    if (!employee) {
      return true;
    }
    if (pageUser !== null && this.hasRole(user, ['ADMIN'])) {
      return user.employee?._id !== pageUser.employee?._id;
    }

    return !!employee.canEditPaymentDate;
  }

  // TODO: describe the purpose of hosts parameter and rename it to be more descriptive
  static canSee(user: User, hosts?: string[]): boolean {
    if (this.isOwnerOrAdmin(user)) {
      return true;
    } else {
      if (!hosts) {
        return true;
      }
      const employee = user.employee;
      if (!employee) return false;

      for (let i = 0; i < hosts.length; i++) {
        const host = hosts[i];
        if (host === employee._id) {
          return true;
        }
      }
      return false;
    }
  }

  static hasReadOnlyAccess(user: User): boolean {
    if (!user || !user.employee) return false;
    return user.employee?.readOnly === true;
  }

  static isMyEntity(user: User, hosts?: string[]): boolean {
    if (!user.employee) {
      return false;
    } else {
      if (!hosts) {
        return false;
      }
      const employee = user.employee;
      for (let i = 0; i < hosts.length; i++) {
        const host = hosts[i];
        if (host === employee._id) {
          return true;
        }
      }
      return false;
    }
  }

  static canNotify(owner: User, client: StudioClient): boolean {
    if (client.email) {
      return true;
    } else if (owner.sendSms) {
      return client.phone !== undefined;
    } else {
      return !!client.telegram || !!client.viber;
    }
  }

  static isLoggedIn(user?: User | null): boolean {
    if (!user) {
      return false;
    }
    if (user === null) {
      return false;
    }
    return !!user._id;
  }

  static isOwnerClient(user: User | null, ownerId: string): boolean {
    if (!user) {
      return false;
    }
    if (user === null) {
      return false;
    }
    if (!user.studioClients) {
      return false;
    }
    for (const studioClient of user.studioClients) {
      if (studioClient.ownerId === ownerId) {
        return true;
      }
    }
    return false;
  }

  static isWlGelios(user?: User | null): boolean {
    return user?.clientCabinet === 'gelios';
  }

  static getStudioClient(user: User | null, ownerId: string): Studio | undefined {
    if (!user) {
      return undefined;
    }
    if (user === null) {
      return undefined;
    }
    if (!user.studioClients) {
      return undefined;
    }
    for (const studioClient of user.studioClients) {
      if (studioClient.ownerId === ownerId) {
        return studioClient;
      }
    }
    return undefined;
  }

  static isStudioOwner(user: User): boolean {
    return UserHelper.isLoggedIn(user) && user.role === UserHelper.STUDIO_OWNER;
  }

  static isStudent(user: User): boolean {
    return UserHelper.isLoggedIn(user) && user.role === UserHelper.STUDENT;
  }

  static isRole(user: User, roles: number[]): boolean {
    if (!UserHelper.isLoggedIn(user)) {
      return false;
    }

    for (let i = 0; i < roles.length; i++) {
      const role = roles[i];
      if (user.role === role) {
        return true;
      }
    }
    return false;
  }

  static getStudioClientId(user: User | null, studio: Studio): string | undefined {
    if (!user) {
      return undefined;
    }
    if (!studio) {
      return undefined;
    }
    if (!user.studioClients) {
      return undefined;
    }

    // Fix for accessing studioClients with owner_id as key
    for (const client of user.studioClients) {
      if (client.ownerId === studio.owner_id) {
        return client._id;
      }
    }
    return undefined;
  }

  static getStudioClientIds(user: User | null): string[] | undefined {
    if (!user) {
      return undefined;
    }
    if (!user.studioClients) {
      return undefined;
    }
    const result: string[] = [];

    // Fix for accessing studioClients
    for (const client of user.studioClients) {
      result.push(client._id);
    }
    return result;
  }

  static canCreateTags(user: User): boolean {
    if (UserHelper.isOwner(user)) {
      return true;
    }
    if (user.employee && user.employee.canCreateTags) {
      return true;
    }

    return false;
  }

  static canAddNewEmployee(user: User): boolean {
    if (UserHelper.isOwner(user)) {
      return true;
    }
    if (UserHelper.isAdmin(user) && user.employee?.canAddNewEmployee) {
      return true;
    }

    return false;
  }

  static getTermsAndConditions(owner?: User | null): string | undefined {
    this.savePreviousPage();
    const termsAndConditions = owner?.termsAndConditions;
    const termsAndConditionsHtml = owner?.termsAndConditionsHtml;
    const owner_id = owner?._id;

    if (termsAndConditionsHtml && owner_id) {
      return `/${owner_id}/terms-and-conditions-html`;
    } else if (termsAndConditions) {
      return termsAndConditions;
    }
    return undefined;
  }

  static getClientCabinet(user: User): string | undefined {
    return user.clientCabinet;
  }

  static canUseAI(owner: User): boolean {
    return !!owner.canUseAI;
  }

  static canUseAdminPanel(user: User): boolean {
    return user.type === 'CONTENT_MANAGER' && !!user.adminPanel;
  }

  // SUPER_USER: a CONTENT_MANAGER who may also suspend/resume the whole platform. UI hint only -
  // the server checks the flag itself (core/auth-manager.js's requireSuperUser).
  static isSuperUser(user: User): boolean {
    return user.type === 'CONTENT_MANAGER' && user.superUser === true;
  }

  static canSeeTheirStudents(user: User, client: any): boolean {
    if (!user.employee) {
      return true;
    }

    const roles = user.employee.roles || [];

    if (roles.length > 1 || roles.includes('OWNER') || roles.includes('ADMIN')) {
      return true;
    }

    if (roles.length === 1 && roles.includes('TEACHER')) {
      return !client.teacherIds || client.teacherIds.length === 0 || client.teacherIds.includes(user.employee._id);
    }

    return true;
  }

  static savePreviousPage(): void {
    localStorage.setItem('savedPreviousPage', window.location.pathname);
  }
}
